import React, { createContext, useContext, useState, useEffect } from 'react';

const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(null); // { message: '', type: 'success' }
  const [modal, setModal] = useState(null); // { message: '', title: '', type: 'info' }
  const [confirm, setConfirm] = useState(null); // { message: '', title: '', type: 'warning', resolve }

  // Auto close toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const showModal = (message, title = 'Notifikasi', type = 'info') => {
    setModal({ message, title, type });
  };

  const closeModal = () => {
    setModal(null);
  };

  const showConfirm = (message, title = 'Konfirmasi', type = 'warning') => {
    return new Promise((resolve) => {
      setConfirm({
        message,
        title,
        type,
        resolve,
      });
    });
  };

  return (
    <ToastContext.Provider value={{ showToast, showModal, showConfirm }}>
      {children}

      {/* Top Center Bootstrap Toast */}
      {toast && (
        <div
          className='position-fixed top-0 start-50 translate-middle-x p-3'
          style={{ zIndex: 1100, minWidth: '320px' }}
        >
          <div
            className={`toast show align-items-center text-white bg-${toast.type === 'danger' ? 'danger' : toast.type === 'warning' ? 'warning' : 'success'} border-0 shadow-lg`}
            role='alert'
            aria-live='assertive'
            aria-atomic='true'
          >
            <div className='d-flex'>
              <div className='toast-body d-flex align-items-center gap-2'>
                <i
                  className={`bi ${toast.type === 'danger' ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} fs-5`}
                ></i>
                <div className='fw-medium'>{toast.message}</div>
              </div>
              <button
                type='button'
                className='btn-close btn-close-white me-2 m-auto'
                onClick={() => setToast(null)}
                aria-label='Close'
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Small Bootstrap Modal Dialog */}
      {modal && (
        <>
          <div
            className='modal fade show d-block'
            tabIndex='-1'
            role='dialog'
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1090 }}
          >
            <div
              className='modal-dialog modal-sm modal-dialog-centered'
              role='document'
            >
              <div className='modal-content border-0 shadow-lg rounded-4'>
                <div
                  className={`modal-header bg-${modal.type} text-white border-0 py-2 px-3 rounded-top-4`}
                >
                  <h6 className='modal-title fw-bold m-0 d-flex align-items-center gap-2'>
                    <i
                      className={`bi ${modal.type === 'danger' ? 'bi-exclamation-octagon-fill' : 'bi-info-circle-fill'}`}
                    ></i>
                    {modal.title}
                  </h6>
                  <button
                    type='button'
                    className='btn-close btn-close-white'
                    onClick={closeModal}
                    aria-label='Close'
                  ></button>
                </div>
                <div className='modal-body p-4 text-center'>
                  <p className='mb-0 text-secondary fw-medium'>
                    {modal.message}
                  </p>
                </div>
                <div className='modal-footer border-0 p-2 justify-content-center'>
                  <button
                    type='button'
                    className={`btn btn-${modal.type} btn-sm rounded-pill px-4 fw-medium`}
                    onClick={closeModal}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className='modal-backdrop fade show'
            style={{ zIndex: 1080 }}
          ></div>
        </>
      )}

      {/* Premium Confirm Dialog Modal */}
      {confirm && (
        <>
          <div
            className='modal fade show d-block'
            tabIndex='-1'
            role='dialog'
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1090 }}
          >
            <div
              className='modal-dialog modal-dialog-centered'
              style={{ maxWidth: '400px' }}
              role='document'
            >
              <div className='modal-content border-0 shadow-lg rounded-4 overflow-hidden'>
                <div className='p-4 text-center'>
                  {/* Warning/Danger Circle Icon Indicator */}
                  <div
                    className={`mx-auto mb-3 d-flex align-items-center justify-content-center bg-${confirm.type === 'danger' ? 'danger-subtle text-danger' : confirm.type === 'warning' ? 'warning-subtle text-warning' : 'primary-subtle text-primary'} rounded-circle`}
                    style={{ width: '64px', height: '64px' }}
                  >
                    <i
                      className={`bi ${confirm.type === 'danger' ? 'bi-trash3-fill' : confirm.type === 'warning' ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'} fs-2`}
                    ></i>
                  </div>

                  {/* Title & Description */}
                  <h5 className='fw-bold mb-2 text-dark'>{confirm.title}</h5>
                  <p className='mb-4 text-secondary small px-2'>
                    {confirm.message}
                  </p>

                  {/* Action Buttons */}
                  <div className='d-flex gap-2 justify-content-center'>
                    <button
                      type='button'
                      className='btn btn-light rounded-pill px-4 py-2 border fw-medium text-secondary'
                      style={{ fontSize: '0.9rem' }}
                      onClick={() => {
                        confirm.resolve(false);
                        setConfirm(null);
                      }}
                    >
                      Batal
                    </button>
                    <button
                      type='button'
                      className={`btn btn-${confirm.type === 'danger' ? 'danger' : confirm.type === 'warning' ? 'warning' : 'primary'} rounded-pill px-4 py-2 fw-semibold text-white shadow-sm`}
                      style={{ fontSize: '0.9rem' }}
                      onClick={() => {
                        confirm.resolve(true);
                        setConfirm(null);
                      }}
                    >
                      Ya, Lanjutkan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className='modal-backdrop fade show'
            style={{ zIndex: 1080 }}
          ></div>
        </>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
